import React, { useEffect, useMemo, useState } from 'react';
import '@progress/kendo-theme-default/dist/all.css';
import '../theme.css';
import { Button } from '@progress/kendo-react-buttons';
import { createFieldTypeRegistry, type FieldTypeDefinition, type FieldTypeRegistry } from '../fieldTypes/registry';
import type { AnswerMap, AnswerValue, Field, FormDocument, Section } from '../types';
import { fieldWidthPercent } from '../utils/fieldWidth';
import { InjectedDataContext } from '../context/InjectedDataContext';

export interface FormRendererCallbacks {
  onAnswerChange?: (handle: string, value: AnswerValue) => void;
  onSubmit?: (answers: AnswerMap) => void;
  /**
   * Called by InjectedDataField renderers on mount to fetch external data.
   * Receives the injection type key (from INJECTED_DATA_OPTIONS) and must
   * return an HTML string (or plain text) to display in the field.
   */
  onResolveInjectedData?: (injectionType: string) => Promise<string>;
}

export interface FormRendererProps {
  document: FormDocument;
  mode: 'preview' | 'fill';
  initialAnswers?: AnswerMap;
  /** Extend/override the built-in field type registry — same contract as <FormBuilder fieldTypes>. */
  fieldTypes?: FieldTypeDefinition[];
  callbacks?: FormRendererCallbacks;
  readOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

function passesConditional(field: Field, answers: AnswerMap): boolean {
  if (!field.conditional) return true;
  const matches = String(answers[field.conditional.sourceHandle] ?? '') === field.conditional.value;
  return field.conditional.operator === 'equals' ? matches : !matches;
}

function SectionView({
  section,
  allSections,
  registry,
  answers,
  readOnly,
  onChange,
}: {
  section: Section;
  allSections: Section[];
  registry: FieldTypeRegistry;
  answers: AnswerMap;
  readOnly: boolean;
  onChange: (handle: string, value: AnswerValue) => void;
}) {
  const sortedFields = [...section.fields].sort((a, b) => a.order - b.order);
  // Replaces legacy Category.parent_id structural nesting — see SectionBlock in the builder.
  const childSections = allSections.filter((s) => s.parentId === section.id).sort((a, b) => a.order - b.order);

  return (
    <div style={{ marginBottom: 'var(--kendo-spacing-6)' }}>
      {(section.label || section.description) && (
        <div style={{ marginBottom: 'var(--kendo-spacing-3)' }}>
          {section.label && <h3 style={{ margin: 0, fontSize: 'var(--kendo-font-size-lg)' }}>{section.label}</h3>}
          {section.description && (
            <p style={{ margin: 'var(--kendo-spacing-1) 0 0', fontSize: 'var(--kendo-font-size-sm)', color: 'var(--kendo-color-subtle)' }}>
              {section.description}
            </p>
          )}
        </div>
      )}
      {/* Mirrors the builder canvas's wrapping row layout (see SectionBlock) — fields whose
          configured widths sum to <= 100% sit side by side instead of always stacking, and
          this must match the design-time layout exactly so preview shows the real result. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--kendo-spacing-4)' }}>
        {sortedFields
          .filter((f) => !f.isHidden && passesConditional(f, answers))
          .map((field) => {
            const def = registry[field.fieldType];
            if (!def) return null;
            return (
              <div key={field.id} style={{ flex: `0 1 ${fieldWidthPercent(field.layout?.width)}%`, minWidth: 0, boxSizing: 'border-box' }}>
                {!def.isDisplayOnly && (
                  <label style={{ display: 'block', fontSize: 'var(--kendo-font-size-sm)', fontWeight: 'var(--kendo-font-weight-bold)', marginBottom: 'var(--kendo-spacing-1)' }}>
                    {field.label}
                    {field.isRequired && <span style={{ color: 'var(--kendo-color-error)' }}> *</span>}
                  </label>
                )}
                <def.Renderer
                  field={field}
                  value={answers[field.handle] ?? null}
                  onChange={(v) => onChange(field.handle, v)}
                  readOnly={readOnly}
                  answers={answers}
                />
              </div>
            );
          })}
      </div>
      {childSections.map((child) => (
        <div key={child.id} style={{ marginLeft: 'var(--kendo-spacing-5)' }}>
          <SectionView section={child} allSections={allSections} registry={registry} answers={answers} readOnly={readOnly} onChange={onChange} />
        </div>
      ))}
    </div>
  );
}

/**
 * Public runtime component — renders a FormDocument as an actual fillable
 * form. Used both for the Preview drawer inside FormBuilder (mode="preview")
 * and for a real end-user fill-out experience (mode="fill"), so preview is
 * guaranteed to show exactly what production renders.
 */
export function FormRenderer({
  document,
  mode,
  initialAnswers,
  fieldTypes,
  callbacks,
  readOnly = false,
  style,
  className,
}: FormRendererProps) {
  const registry = useMemo(() => createFieldTypeRegistry(fieldTypes), [fieldTypes]);
  const [answers, setAnswers] = useState<AnswerMap>(initialAnswers ?? {});

  useEffect(() => {
    setAnswers(initialAnswers ?? {});
  }, [initialAnswers]);

  function handleChange(handle: string, value: AnswerValue) {
    setAnswers((prev) => ({ ...prev, [handle]: value }));
    callbacks?.onAnswerChange?.(handle, value);
  }

  const rootSections = document.sections.filter((s) => !s.parentId).sort((a, b) => a.order - b.order);

  return (
    <InjectedDataContext.Provider value={callbacks?.onResolveInjectedData}>
    <div
      className={className}
      style={{
        padding: 'var(--kendo-spacing-4)',
        background: 'var(--kendo-color-app-surface)',
        color: 'var(--kendo-color-on-app-surface)',
        ...style,
      }}
      data-fulcrum-form-renderer
      data-mode={mode}
    >
      {mode === 'preview' && (
        <div
          style={{
            marginBottom: 'var(--kendo-spacing-4)',
            padding: 'var(--kendo-spacing-2) var(--kendo-spacing-3)',
            borderRadius: 'var(--kendo-border-radius-md)',
            background: 'var(--kendo-color-warning-subtle)',
            border: '1px solid var(--kendo-color-warning)',
            color: 'var(--kendo-color-warning-on-subtle)',
            fontSize: 'var(--kendo-font-size-sm)',
            fontWeight: 'var(--kendo-font-weight-bold)',
          }}
        >
          Preview — this version is not yet published
        </div>
      )}

      {rootSections.map((section) => (
        <SectionView
          key={section.id}
          section={section}
          allSections={document.sections}
          registry={registry}
          answers={answers}
          readOnly={readOnly}
          onChange={handleChange}
        />
      ))}

      {mode === 'fill' && !readOnly && (
        <Button themeColor="primary" onClick={() => callbacks?.onSubmit?.(answers)}>
          Submit
        </Button>
      )}
    </div>
    </InjectedDataContext.Provider>
  );
}
